import MessengerModal from "./messengerModal";
import React, {useEffect, useMemo, useState} from "react";
import {Row} from "reactstrap";
import Col from "reactstrap/es/Col";
import {SmartGroupVertical} from "@csas-smart/smart-ui-comps";
import {useDispatch, useSelector} from "react-redux";
import {fireChangeTopic, fireGetTopics} from "../../actions/messengerActions";
import ActionsModalFooter from "./actionsModalFooter";
import {Alert, FilteredSelect, Spinner} from "@george-labs.com/design-system";

export const ActionsChangeTopicModal = ({isOpen, onToggle}) => {

    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoadingTopics, setIsLoadingTopics] = useState(false);

    const changingTopic = useSelector(state => state?.humanTask?.changingTopic);
    const cidla = useSelector(state => state?.messenger?.cidla);
    const legalCluid = useSelector(state => state?.messenger?.company?.legalCluid);
    const threadId = useSelector(state => state?.messenger?.thread.id);
    const currentTopicHints = useSelector(state => {
        const t = state?.messenger?.thread;
        const ht = state?.humanTask?.humanTask;
        const mergedSubject = state?.messenger?.mergedSubject;
        return {
            id:
                t?.topicId ||
                t?.topic?.id ||
                (typeof t?.topic === 'string' ? t.topic : null) ||
                ht?.topicId ||
                ht?.topic?.id ||
                (typeof ht?.topic === 'string' ? ht.topic : null) ||
                null,
            label:
                t?.topicLabel ||
                t?.topic?.label ||
                t?.subject ||
                mergedSubject ||
                ht?.topicLabel ||
                ht?.topic?.label ||
                null,
            // Keep raw refs for debugging
            _thread: t,
            _humanTask: ht
        };
    });
    const dispatch = useDispatch();

    useEffect(() => {
        if (isOpen) {
            setIsLoadingTopics(true);
            setLoadingError(false);
            setSelectedTopic(null);
            setTopics([]);

            dispatch(fireGetTopics(legalCluid))
                .then(topicsData => {
                    if (topicsData && topicsData.length > 0) {
                        setTopics(topicsData);
                    } else {
                        setLoadingError(true);
                    }
                    setIsLoadingTopics(false);
                })
                .catch(err => {
                    console.log("Error loading topics:", err);
                    setLoadingError(true);
                    setIsLoadingTopics(false);
                });
        }
    }, [isOpen, cidla, legalCluid, dispatch]);

    const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

    const isCurrentTopic = (topic) => {
        const topicLabel = topic.label || topic.name || '';
        return (
            (currentTopicHints?.id && topic.id === currentTopicHints.id) ||
            (currentTopicHints?.label && norm(currentTopicHints.label) === norm(topicLabel))
        );
    };

    // Build options for the George Select combobox.
    // Labels are plain strings so the Select's built-in type-to-filter search works.
    const items = useMemo(() => {
        return topics.map((topic) => {
            const topicLabel = topic.label || topic.name || '';
            const isCurrent = isCurrentTopic(topic);
            return {
                value: topic.id,
                disabled: isCurrent,
                label: isCurrent ? `${topicLabel} (aktuální téma)` : topicLabel
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topics, currentTopicHints]);

    const handleSelectTopic = (selectedItem) => {
        if (!selectedItem || selectedItem.disabled) {
            return;
        }
        const topic = topics.find(t => t.id === selectedItem.value);
        setSelectedTopic(topic || null);
    };

    const handleSubmit = () => {
        if (selectedTopic) {
            dispatch(fireChangeTopic(cidla, threadId, selectedTopic.id)).then(() => handleToggle());
        }
    };

    const handleToggle = () => {
        setSelectedTopic(null);
        setTopics([]);
        setLoadingError(false);
        setIsLoadingTopics(false);
        onToggle();
    };

    const getBody = () => {
        if (isLoadingTopics) {
            return (
                <div className="text-center py-4">
                    <Spinner size="large" text="Načítám témata..."/>
                </div>
            );
        }

        if (loadingError) {
            return (
                <Alert variant={Alert.VARIANT.ERROR}>
                    Nepodařilo se načíst dostupná témata. Prosím, zkuste to později.
                </Alert>
            );
        }

        if (topics.length === 0) {
            return (
                <Alert variant={Alert.VARIANT.INFO}>
                    Nejsou dostupná žádná témata.
                </Alert>
            );
        }

        return (
            <Row>
                <Col>
                    <div className="topic-combobox">
                        <SmartGroupVertical id="sgv-search" label="Téma"
                                            target="st-search">
                            <FilteredSelect
                                id="st-search"
                                fullWidth
                                placeholder="Vyberte téma"
                                emptyLabel="Nebyla nalezena žádná témata."
                                items={items}
                                value={selectedTopic?.id || ''}
                                itemToString={(item) => (item && item.label ? item.label : '')}
                                highlightText
                                menuClassName="topic-combobox__menu"
                                dropdownClassName="topic-combobox__dropdown"
                                onBeforeSelect={(item) => !(item && item.disabled)}
                                onSelect={handleSelectTopic}
                            />
                        </SmartGroupVertical>
                    </div>
                </Col>
            </Row>
        );
    };

    const getFooter = () => {
        let primary = {
            onClick: handleSubmit,
            text: 'Přesunout',
            disabled: !selectedTopic || isLoadingTopics || loadingError || changingTopic?.progress
        };

        let cancel = {
            onClick: handleToggle,
            text: 'Pokračovat v konverzaci'
        };

        return (
            <ActionsModalFooter primary={primary} cancel={cancel}/>
        );
    };

    return (
        <MessengerModal
            size="sm"
            processing={changingTopic?.progress}
            processingText='Měním téma, počkejte prosím'
            error={changingTopic?.error}
            isOpen={isOpen}
            onToggle={handleToggle}
            header="Přesunout pod jiné téma"
            body={getBody()}
            footer={getFooter()}
        />
    );
};

export default ActionsChangeTopicModal;
